import { useState, useEffect, useCallback } from 'react';
import { InputNumber, Radio, Select, TimePicker } from 'antd';
import FormField from 'components/FormField';
import produce from 'immer';
import TransComponent from 'components/TransComponent';
import { css } from '@emotion/css';
import { useLatest } from 'ahooks';
import { useField } from 'formik';
import dayjs from 'lib/dayjs';
import { T, useTranslate } from '@tolgee/react';

type LogisticsSendOutTimeProps = {
  initialValue?: Knowledge.LogisticsSendOutTime;
  fieldNamePrefix: string;
};

function safeParse(value: string) {
  try {
    return JSON.parse(value);
  } catch (e) {
    return undefined;
  }
}

const getFieldName = (name: string, prefix: string) => {
  return prefix ? `${prefix}.${name}` : name;
};

function LogisticsSendOutTime(props: LogisticsSendOutTimeProps) {
  const { fieldNamePrefix, initialValue } = props;
  const [cacheValue, setCacheValue] = useState(initialValue);
  const [{ value: templateCode }] = useField(getFieldName('templateCode', fieldNamePrefix));
  const { t } = useTranslate();

  const getFieldInitialValue = useCallback(
    (code: string, fieldValue) => {
      const fv = safeParse(fieldValue);
      if (templateCode === code) {
        return fv || initialValue?.[code];
      }
      return initialValue?.[code];
    },
    [templateCode, initialValue],
  );

  const [, , helpers] = useField(getFieldName('content', fieldNamePrefix));
  const onFieldChange = useCallback(
    (code, value) => {
      if (code === templateCode) {
        helpers.setValue(JSON.stringify(value));
        setCacheValue(
          produce((draft) => {
            draft[code] = value;
          }),
        );
      } else {
        setCacheValue(
          produce((draft) => {
            draft[code] = value;
          }),
        );
      }
    },
    [helpers, setCacheValue, templateCode],
  );

  return (
    <FormField name={getFieldName('templateCode', fieldNamePrefix)}>
      {({ field }) => {
        return (
          <Radio.Group
            value={field.value}
            onChange={(e) => {
              const code = e.target.value;
              field.onChange(e);
              setTimeout(() => {
                helpers.setValue(JSON.stringify(cacheValue[code]));
              });
            }}
            className={RadioGroup}
          >
            <div>
              <Radio value="UNDER_MAX" />
              <div>
                <FormField
                  name={
                    templateCode === 'UNDER_MAX'
                      ? getFieldName('content', fieldNamePrefix)
                      : 'i-dont-care'
                  }
                  validate={(value) => {
                    try {
                      const v = JSON.parse(value);
                      if (!v?.max) {
                        return '必填';
                      }
                    } catch (e) {
                      return undefined;
                    }
                  }}
                >
                  {({ field }) => {
                    return (
                      <UnderMaxFormItem
                        value={getFieldInitialValue('UNDER_MAX', field.value)}
                        onChange={(value) => {
                          onFieldChange('UNDER_MAX', value);
                        }}
                      />
                    );
                  }}
                </FormField>
              </div>
            </div>
            <div>
              <Radio value="RANGE" />
              <div>
                <FormField
                  name={
                    templateCode === 'RANGE'
                      ? getFieldName('content', fieldNamePrefix)
                      : 'i-dont-care'
                  }
                  validate={(value) => {
                    try {
                      const v = JSON.parse(value);
                      if (!v?.rangeMin || !v?.rangeMax) {
                        return '必填';
                      }
                    } catch (e) {
                      return undefined;
                    }
                  }}
                >
                  {({ field }) => {
                    return (
                      <RangeFormItem
                        value={getFieldInitialValue('RANGE', field.value)}
                        onChange={(value) => onFieldChange('RANGE', value)}
                      />
                    );
                  }}
                </FormField>
              </div>
            </div>
            <div>
              <Radio value="TODAY_OR_TOMORROW_ON_TIME" />
              <div>
                <FormField
                  name={
                    templateCode === 'TODAY_OR_TOMORROW_ON_TIME'
                      ? getFieldName('content', fieldNamePrefix)
                      : 'i-dont-care'
                  }
                  validate={(value) => {
                    try {
                      const v = JSON.parse(value);
                      if (!v?.today_or_tomorrow_time) {
                        return '必填';
                      }
                    } catch (e) {
                      return undefined;
                    }
                  }}
                >
                  {({ field }) => {
                    return (
                      <TodayOrTomorrowOnTimeFormItem
                        value={getFieldInitialValue('TODAY_OR_TOMORROW_ON_TIME', field.value)}
                        onChange={(value) => onFieldChange('TODAY_OR_TOMORROW_ON_TIME', value)}
                      />
                    );
                  }}
                </FormField>
              </div>
            </div>
            <div>
              <Radio value="AS_SOON">{t('knowledge.会尽快发货')}</Radio>
            </div>
          </Radio.Group>
        );
      }}
    </FormField>
  );
}

type FormItem<T> = {
  value?: T;
  onChange?: (value: T) => void;
};

export const useDayTypeSelectOptions = () => {
  const { t } = useTranslate();
  return [
    {
      value: 'WORK_DAY',
      label: t('knowledge.WORK_DAY'),
    },
    {
      value: 'DAY',
      label: t('knowledge.DAY'),
    },
    {
      value: 'HOUR',
      label: t('knowledge.HOUR'),
    },
  ];
};

function UnderMaxFormItem(props: FormItem<{ max: number; unit: string }>) {
  const { value, onChange } = props;
  const [actValue, setActValue] = useState(value);
  const onChangeRef = useLatest(onChange);

  useEffect(() => {
    onChangeRef?.current(actValue);
  }, [actValue, onChangeRef]);

  const dayTypeSelectOptions = useDayTypeSelectOptions().slice(0, 2);

  return (
    <T
      keyName="knowledge.xDay以内"
      params={{
        x: (
          <TransComponent
            content={
              <InputNumber
                min={1}
                max={99}
                value={actValue?.max}
                onChange={(value) => {
                  setActValue(
                    produce((draft) => {
                      draft.max = value;
                    }),
                  );
                }}
              />
            }
          />
        ),
        day: (
          <TransComponent
            content={
              <Select
                value={actValue?.unit}
                options={dayTypeSelectOptions}
                dropdownMatchSelectWidth={false}
                onChange={(value) => {
                  setActValue(
                    produce((draft) => {
                      draft.unit = value;
                    }),
                  );
                }}
              />
            }
          />
        ),
      }}
    />
  );
}

function RangeFormItem(props: FormItem<{ rangeMin: number; rangeMax: number; unit: string }>) {
  const { value, onChange } = props;
  const [actValue, setActValue] = useState(value);
  const onChangeRef = useLatest(onChange);

  useEffect(() => {
    onChangeRef?.current(actValue);
  }, [actValue, onChangeRef]);

  const dayTypeSelectOptions = useDayTypeSelectOptions();
  return (
    <T
      keyName="knowledge.商品发货天数range"
      params={{
        x: (
          <TransComponent
            content={
              <InputNumber
                min={0}
                max={actValue?.rangeMax - 1}
                value={actValue?.rangeMin}
                onChange={(value) => {
                  setActValue(
                    produce((draft) => {
                      draft.rangeMin = value;
                    }),
                  );
                }}
              />
            }
          />
        ),
        y: (
          <TransComponent
            content={
              <InputNumber
                min={actValue?.rangeMin + 1}
                max={199}
                value={actValue?.rangeMax}
                onChange={(value) => {
                  setActValue(
                    produce((draft) => {
                      draft.rangeMax = value;
                    }),
                  );
                }}
              />
            }
          />
        ),
        day: (
          <TransComponent
            content={
              <Select
                value={actValue?.unit}
                options={dayTypeSelectOptions}
                dropdownMatchSelectWidth={false}
                onChange={(value) => {
                  setActValue(
                    produce((draft) => {
                      draft.unit = value;
                    }),
                  );
                }}
              />
            }
          />
        ),
      }}
    />
  );
}

function TodayOrTomorrowOnTimeFormItem(props: FormItem<{ today_or_tomorrow_time: string }>) {
  const { value, onChange } = props;
  const [actValue, setActValue] = useState(value);
  const onChangeRef = useLatest(onChange);
  const momentValue = dayjs(actValue?.today_or_tomorrow_time, 'HH:mm');

  useEffect(() => {
    onChangeRef?.current(actValue);
  }, [actValue, onChangeRef]);

  const handleChange = (value) => {
    setActValue(
      produce((draft) => {
        draft.today_or_tomorrow_time = value.format('HH:mm');
      }),
    );
  };

  const picker = (
    <TimePicker
      allowClear={false}
      format="HH:mm"
      className="w-[90px]"
      value={momentValue}
      onChange={handleChange}
    />
  );

  return (
    <T
      keyName="knowledge.x 前当天发货 & y 后次日发货"
      params={{
        x: <TransComponent content={picker} />,
        y: <TransComponent content={picker} />,
      }}
    />
  );
}

const RadioGroup = css`
  font-size: unset;
  > div {
    display: flex;
    align-items: flex-start;
    .ant-form-item {
      margin-bottom: 0;
    }
    & + div {
      margin-top: 12px;
    }
    > label {
      height: 100%;
      display: block;
      flex: 0 0 auto;
    }
    > div {
      flex: 1 0 0;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      & .ant-form-item-control-input-content {
        > * {
          margin-right: 4px;
          & + * {
            margin-left: 4px;
          }
        }
      }
      .ant-select {
        width: auto;
      }
    }
  }
`;

export default LogisticsSendOutTime;
